import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ApiFetch, CreateAsyncStatusHandler, ResetSliceState } from "../../app/SliceUtils";

// Attendance
export const fetchAllAttendances = createAsyncThunk(
    '/api/attendance/currentTodo',
    ({ completed, departmentId }, { rejectWithValue }) =>
        ApiFetch({
            endpoint: departmentId !== null ? `api/attendance/${completed}/${departmentId}` : `api/attendance/${completed}`,
            contentType: 'application/x-www-form-urlencoded'
        }, rejectWithValue)
);

export const fetchPerformanceAttendance = createAsyncThunk(
    '/api/attendance/performance/performanceId',
    (performanceId, { rejectWithValue }) =>
        ApiFetch({
            endpoint: `api/attendance/performance/${performanceId}`,
            contentType: 'application/x-www-form-urlencoded'
        }, rejectWithValue)
);

export const fetchMomentAttendance = createAsyncThunk(
    '/api/attendance/moment/momentId',
    (momentId, { rejectWithValue }) =>
        ApiFetch({
            endpoint: `api/attendance/moment/${momentId}`,
            contentType: 'application/x-www-form-urlencoded'
        }, rejectWithValue)
);

export const postAttendances = createAsyncThunk(
    '/api/attendance/moment/post',
    (newAttendances, { rejectWithValue }) =>
        ApiFetch({
            endpoint: `api/attendance/moment`,
            method: 'POST',
            body: newAttendances
        }, rejectWithValue)
);

// Authorization
export const fetchEmployeesForAuthorization = createAsyncThunk(
    '/api/attendance/employees/new/performanceId',
    (performanceId, { rejectWithValue }) =>
        ApiFetch({
            endpoint: `api/attendance/employees/new/${performanceId}`,
            contentType: 'application/x-www-form-urlencoded'
        }, rejectWithValue)
);

export const fetchExistingAuthorizations = createAsyncThunk(
    '/api/attendance/employees/existing/performanceId',
    (performanceId, { rejectWithValue }) =>
        ApiFetch({
            endpoint: `api/attendance/employees/existing/${performanceId}`,
            contentType: 'application/x-www-form-urlencoded'
        }, rejectWithValue)
);

export const postEmployeeAuthorizations = createAsyncThunk(
    '/api/attendance/authorizeEmployee/post',
    (employeeAuthorizations, { rejectWithValue }) =>
        ApiFetch({
            endpoint: `api/attendance/authorizeEmployee`,
            method: 'POST',
            body: employeeAuthorizations
        }, rejectWithValue)
);

export const deleteAuthorization = createAsyncThunk(
    '/api/attendance/delete',
    ({ performanceId, employeeId }, { rejectWithValue }) =>
        ApiFetch({
            endpoint: `api/attendance/${performanceId}/${employeeId}`,
            method: 'DELETE'
        }, rejectWithValue)
);

// Performance attendance list
export const fetchAttendanceList = createAsyncThunk(
    '/api/attendance/list/performanceId',
    (performanceId, { rejectWithValue }) =>
        ApiFetch({
            endpoint: `api/attendance/list/${performanceId}`,
            contentType: 'application/x-www-form-urlencoded'
        }, rejectWithValue)
);

// Group attendance
export const fetchGroupAttendanceSchoolclasses = createAsyncThunk(
    '/api/attendance/groupAttendance/schoolclasses',
    (_, { rejectWithValue }) =>
        ApiFetch({
            endpoint: `api/attendance/groupAttendance/schoolclasses`,
            contentType: 'application/x-www-form-urlencoded'
        }, rejectWithValue)
);

export const postGroupAttendance = createAsyncThunk(
    '/api/attendance/groupAttendance/post',
    (newGroupAttendance, { rejectWithValue }) =>
        ApiFetch({
            endpoint: `api/attendance/groupAttendance`,
            method: 'POST',
            body: newGroupAttendance
        }, rejectWithValue)
);

// Delete attendance
export const deleteAttendances = createAsyncThunk(
    '/api/attendance/attendances/momentId',
    (momentId, { rejectWithValue }) =>
        ApiFetch({
            endpoint: `api/attendance/attendances/${momentId}`,
            method: 'DELETE'
        }, rejectWithValue)
);

export const attendanceSlice = createSlice({
    name: "attendance",
    initialState: {
        // Attendance
        attendances: { data: null, status: null },
        attendancesPerformance: { data: null, status: null },
        attendanceMoment: { data: null, status: null },
        postAttendance: { data: null, status: null },

        // Authorization
        employeesForAuthorization: { data: null, status: null },
        existingAuthorizations: { data: null, status: null },
        postEmployeeAuthorization: { data: null, status: null },
        deleteAuthorizations: { data: null, status: null },
        showAuthorizationFailed: false,

        // Performance attendance list
        attendanceList: { data: null, status: null },

        // Group attendance
        groupAttendanceSchoolclasses: { data: null, status: null },
        groupAttendancePost: { data: null, status: null },

        // Delete attendance
        attendancesDelete: { data: null, status: null },
    },
    reducers: {
        setFailedAuthorizations: (state, action) => {
            state.showAuthorizationFailed = action.payload;
        },
        setAllPresents: (state, action) => {
            state.attendanceMoment.data.attendances.forEach((item) => (item.present = action.payload));
        },
        setPresent: (state, action) => {
            const index = state.attendanceMoment.data.attendances.findIndex(
                (item) => item.studentId === action.payload.id
            );
            state.attendanceMoment.data.attendances[index].present = action.payload.value;
        },
        setDispensation: (state, action) => {
            const index = state.attendanceMoment.data.attendances.findIndex(
                (item) => item.studentId === action.payload.id
            );
            state.attendanceMoment.data.attendances[index].dispensation = action.payload.value;
        },
        setWorkedHours: (state, action) => {
            const index = state.attendanceMoment.data.attendances.findIndex(
                (item) => item.studentId === action.payload.id
            );
            state.attendanceMoment.data.attendances[index].workedHours = action.payload.value;
        },
        setRemark: (state, action) => {
            const index = state.attendanceMoment.data.attendances.findIndex(
                (item) => item.studentId === action.payload.id
            );
            state.attendanceMoment.data.attendances[index].remark = action.payload.value;
        },
        setAllCreatedBy: (state, action) => {
            state.attendanceMoment.data.attendances.forEach((item) => (item.createdBy = action.payload));
        },
        setAllModifiedBy: (state, action) => {
            state.attendanceMoment.data.attendances.forEach((item) => (item.modifiedBy = action.payload));
        },
        resetAttendanceStatus: (state) => {
            ResetSliceState(state, [
                'attendances',
                'attendanceMoment',
                'postAttendance',
                'postEmployeeAuthorization',
                'employeesForAuthorization'
            ]);
        },
        resetGroupAttendanceStatus: (state) => {
            ResetSliceState(state, [
                'groupAttendancePost'
            ]);
        },
        resetAttendancesDelete: (state) => {
            ResetSliceState(state, [
                'attendancesDelete'
            ]);
        },
    },
    extraReducers: (builder) => {
        // Attendance
        CreateAsyncStatusHandler(builder, fetchAllAttendances, 'attendances');
        CreateAsyncStatusHandler(builder, fetchPerformanceAttendance, 'attendancesPerformance');
        CreateAsyncStatusHandler(builder, fetchMomentAttendance, 'attendanceMoment');
        CreateAsyncStatusHandler(builder, postAttendances, 'postAttendance');

        // Authorization
        CreateAsyncStatusHandler(builder, fetchEmployeesForAuthorization, 'employeesForAuthorization');
        CreateAsyncStatusHandler(builder, fetchExistingAuthorizations, 'existingAuthorizations');
        CreateAsyncStatusHandler(builder, postEmployeeAuthorizations, 'postEmployeeAuthorization');
        CreateAsyncStatusHandler(builder, deleteAuthorization, 'deleteAuthorizations');

        // Performance attendance list
        CreateAsyncStatusHandler(builder, fetchAttendanceList, 'attendanceList');

        // Group attendance
        CreateAsyncStatusHandler(builder, fetchGroupAttendanceSchoolclasses, 'groupAttendanceSchoolclasses');
        CreateAsyncStatusHandler(builder, postGroupAttendance, 'groupAttendancePost');

        // Delete attendance
        CreateAsyncStatusHandler(builder, deleteAttendances, 'attendancesDelete');
    },
});

export const {
    setAllPresents,
    setPresent,
    setDispensation,
    setWorkedHours,
    setRemark,
    setAllCreatedBy,
    setAllModifiedBy,
    resetAttendanceStatus,
    setFailedAuthorizations,
    resetGroupAttendanceStatus,
    resetAttendancesDelete
} = attendanceSlice.actions;

export default attendanceSlice.reducer;
